import {
  TIdentObjectTypeEnum,
  TIdentOperationTypeEnum,
} from '@/shared/hooks/use-ident-app-sync-events/use-ident-app-sync-events.types';
import { TInvalidateIdentTagsParams } from '@/shared/hooks/use-invalidate-ident-tags/use-invalidate-ident-tags.types';
import { identApi } from '@/shared/redux/api/ident/ident-api';
import { useAppDispatch } from '@/shared/redux/hooks/hooks';

const LINK_OPERATIONS = [
  TIdentOperationTypeEnum.IdentObjectLinked,
  TIdentOperationTypeEnum.IdentObjectUnlinked,
  TIdentOperationTypeEnum.IdentObjectLinkUpdated,
];

export const useInvalidateIdentTags = () => {
  const dispatch = useAppDispatch();

  const invalidateIdentTags = ({
    identObjectType,
    identObjectId,
    operationType,
    payload,
  }: TInvalidateIdentTagsParams) => {
    if (identObjectType === TIdentObjectTypeEnum.Tenant) {
      // The switch event rides the same channel, but TenantProvider owns it —
      // the switching user reloads into the new tenant rather than refetching.
      if (operationType === TIdentOperationTypeEnum.IdentObjectUpdated) {
        dispatch(identApi.util.invalidateTags(['Tenant']));
      }
      return;
    }

    // The link object carries both sides; the event names only one of them.
    if (LINK_OPERATIONS.includes(operationType)) {
      const userId = payload?.userId ?? identObjectId;
      const userRoleId = payload?.userRoleId;

      dispatch(
        identApi.util.invalidateTags([
          { type: 'User', id: userId },
          { type: 'User', id: 'LIST' },
          { type: 'UserRole', id: 'LIST' },
          // An undefined id degrades to a bare type tag, invalidating every role query.
          ...(userRoleId
            ? [{ type: 'UserRole' as const, id: userRoleId }]
            : []),
        ]),
      );
      return;
    }

    if (identObjectType === TIdentObjectTypeEnum.User) {
      dispatch(
        identApi.util.invalidateTags([
          { type: 'User', id: identObjectId },
          { type: 'User', id: 'LIST' },
          'Groups',
        ]),
      );
      return;
    }

    if (identObjectType === TIdentObjectTypeEnum.UserRole) {
      dispatch(
        identApi.util.invalidateTags([
          { type: 'UserRole', id: identObjectId },
          { type: 'UserRole', id: 'LIST' },
          // Every user row embeds the roles it holds.
          { type: 'User', id: 'LIST' },
          'Groups',
        ]),
      );
    }
  };

  return {
    invalidateIdentTags,
  };
};
